import React, {useEffect} from 'react';

import AemComponent from '@/components/Aem/AemComponent/AemComponent';
import EditableText from '@/components/Aem/EditableText/EditableText';
import {AppButton} from '@/components/Button';
import DeviceIcon from '@/components/DeviceIcon/DeviceIcon';
import {StickyPageLayout} from '@/components/Layout';
import {
    AD_ENTRY_POINT_HEADER_TITLE,
    ADVANCED_DIAGNOSTICS_ENTRY_POINT_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_NOT_NOW_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_NOT_NOW_BUTTON_TEXT,
} from '@/consts/localization/localizationKeys';
import routePaths from '@/consts/route/routePaths';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import {getAriaLabel} from '@/services/accessibilityService';
import amplitudeADTracking from '@/services/analytics/amplitude/amplitudeADTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import appRouterService from '@/services/route/appRouterService';
import routerService from '@/services/route/routerService';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners} from '@/views/WarningBanners';

import AdvancedDiagnosticDescription from '../components/AdvancedDiagnosticDescription';
import AdvancedDiagnosticLayout from '../components/AdvancedDiagnosticLayout';
import AdvancedDiagnosticTitle from '../components/AdvancedDiagnosticTitle';
import NotNowButtonAD from '../components/NotNowButtonAD';

const AdvancedDiagnosticEntryPoint = () => {
    const isMobileLayout = useIsMobileLayout();
    const localizedStrings = getLocalizedStrings();

    const onDiagnosticStart = () => {
        appRouterService.forwardToDamageCheckAD();
    };

    useDocumentTitle(localizedStrings[AD_ENTRY_POINT_HEADER_TITLE]);

    useEffect(() => {
        amplitudeADTracking.trackADEntryPointScreen();
        googleAnalyticsService.trackAdEntryPointScreenView();
    }, []);

    return (
        <AdvancedDiagnosticLayout>
            <AdvancedDiagnosticLayout.ContentWrapper hideHw>
                <StickyPageLayout
                    className='items-center'
                    headerOptions={{
                        onBack: routerService.goBack,
                        title: localizedStrings[AD_ENTRY_POINT_HEADER_TITLE],
                    }}
                    contentClassName='flex flex-1 flex-col items-center'
                    footer={
                        <div className='m-auto flex w-full flex-col items-center gap-4 ml:mt-8 ml:gap-3'>
                            <AppButton
                                primary
                                light
                                onClick={onDiagnosticStart}
                                className='m-auto ml:w-full'
                                aria-label={getAriaLabel(ADVANCED_DIAGNOSTICS_ENTRY_POINT_BUTTON_ARIA_LABEL)}
                            >
                                <AemComponent
                                    component={EditableText}
                                    pageName={routePaths.ADVANCE_DIAGNOSTIC}
                                    itemName='entrypointbutton'
                                />
                            </AppButton>
                            {!isMobileLayout && (
                                <NotNowButtonAD
                                    className='ml:w-full'
                                    aria-label={getAriaLabel(ADVANCED_DIAGNOSTICS_NOT_NOW_BUTTON_ARIA_LABEL)}
                                >
                                    {localizedStrings[ADVANCED_DIAGNOSTICS_NOT_NOW_BUTTON_TEXT]}
                                </NotNowButtonAD>
                            )}
                        </div>
                    }
                >
                    <AdvancedDiagnosticLayout.Container
                        onBackButtonClick={isMobileLayout && routerService.goBack}
                        className='pt-0'
                    >
                        <DeviceIcon mode='diagnostics' className='h-16 w-16' />
                        <AdvancedDiagnosticTitle itemName='entrypointtitle' />
                        <AdvancedDiagnosticDescription itemName='entrypointdesc' />
                    </AdvancedDiagnosticLayout.Container>
                    {isMobileLayout && <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.AD} />}
                </StickyPageLayout>
            </AdvancedDiagnosticLayout.ContentWrapper>
        </AdvancedDiagnosticLayout>
    );
};

export default AdvancedDiagnosticEntryPoint;
